import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { ArrowLeft, Upload01, XCircle, Trash01, ChevronDown, ChevronUp } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { useTranslation } from 'react-i18next';
import { useKnowledgeBase, useUpdateKnowledgeBase, useKnowledgeBaseDocuments, useUploadKnowledgeDocument, useDeleteKnowledgeDocument } from '@/hooks/api/use-knowledge-bases';
import type { KnowledgeBaseUpdate, KnowledgeDocumentAccessLevel, KnowledgeDocumentUploadOptions } from '@/types/api/knowledge-bases';
import type { KnowledgeBaseType } from '@/types/knowledge-base';
import { useToast } from '@/hooks/use-toast';

export const KnowledgeBaseDetailPage = () => {
    const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:knowledge-admin', 'common']);
    const toast = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        kb_type: '' as KnowledgeBaseType | '',
    });
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [uploadOptions, setUploadOptions] = useState<KnowledgeDocumentUploadOptions>({
        priority: undefined,
        access_level: undefined,
        chunking_params: undefined,
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    // API hooks
    const { data: knowledgeBase, isLoading, isError } = useKnowledgeBase(knowledgeBaseId || '');
    const { data: documents = [] } = useKnowledgeBaseDocuments(knowledgeBaseId || '');
    const updateKnowledgeBase = useUpdateKnowledgeBase();
    const uploadDocument = useUploadKnowledgeDocument();
    const deleteDocument = useDeleteKnowledgeDocument();

    const typeOptions = [
        { id: 'general', label: t('types.general') },
        { id: 'documents', label: t('types.documents') },
        { id: 'routes', label: t('types.routes') },
        { id: 'regulation', label: t('types.regulation') },
        { id: 'company/account-specific', label: t('types.companyAccountSpecific') },
    ];

    const priorityOptions = [
        { id: '1', label: '1 - ' + t('form.documents.priority.lowest', { defaultValue: 'Lowest' }) },
        { id: '2', label: '2 - ' + t('form.documents.priority.low', { defaultValue: 'Low' }) },
        { id: '3', label: '3 - ' + t('form.documents.priority.medium', { defaultValue: 'Medium' }) },
        { id: '4', label: '4 - ' + t('form.documents.priority.high', { defaultValue: 'High' }) },
        { id: '5', label: '5 - ' + t('form.documents.priority.highest', { defaultValue: 'Highest' }) },
    ];

    const accessLevelOptions = [
        { id: 'public', label: t('form.documents.accessLevel.public', { defaultValue: 'Public' }) },
        { id: 'company', label: t('form.documents.accessLevel.company', { defaultValue: 'Company' }) },
        { id: 'admin', label: t('form.documents.accessLevel.admin', { defaultValue: 'Admin' }) },
    ];

    // Update form data when knowledge base loads
    useEffect(() => {
        if (knowledgeBase) {
            setFormData({
                name: knowledgeBase.name,
                description: knowledgeBase.description || '',
                kb_type: knowledgeBase.kb_type,
            });
        }
    }, [knowledgeBase]);

    const handleSave = async () => {
        if (!knowledgeBaseId || !formData.name.trim() || !formData.kb_type) {
            toast.error(t('form.validationError', { defaultValue: 'Nome e tipo sono obbligatori' }));
            return;
        }

        try {
            const updateData: KnowledgeBaseUpdate = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                kb_type: formData.kb_type as KnowledgeBaseType,
            };

            await updateKnowledgeBase.mutateAsync({
                knowledgeBaseId,
                data: updateData,
            });

            // Upload new documents if any
            if (uploadFiles.length > 0) {
                // Build options object only if any values are set
                const hasOptions = uploadOptions.priority !== undefined ||
                    uploadOptions.access_level !== undefined ||
                    uploadOptions.chunking_params?.chunk_size !== undefined ||
                    uploadOptions.chunking_params?.chunk_overlap !== undefined;

                for (const file of uploadFiles) {
                    await uploadDocument.mutateAsync({
                        knowledgeBaseId,
                        file,
                        options: hasOptions ? uploadOptions : undefined,
                    });
                }
                setUploadFiles([]);
                setUploadOptions({
                    priority: undefined,
                    access_level: undefined,
                    chunking_params: undefined,
                });
                setShowAdvanced(false);
            }

            toast.success(t('form.updateSuccess', { defaultValue: 'Knowledge base aggiornata con successo' }));
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating knowledge base:', error);
            toast.error(t('form.updateError', { defaultValue: 'Errore nell\'aggiornamento della knowledge base' }));
        }
    };

    const handleDeleteDocument = async (docId: string) => {
        if (!knowledgeBaseId) return;

        try {
            await deleteDocument.mutateAsync({
                knowledgeBaseId,
                documentId: docId,
            });
            toast.success(t('form.documents.deleteSuccess', { defaultValue: 'Documento eliminato con successo' }));
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error(t('form.documents.deleteError', { defaultValue: 'Errore nell\'eliminazione del documento' }));
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        setUploadFiles([...uploadFiles, ...files]);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setUploadFiles([...uploadFiles, ...files]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadFiles(uploadFiles.filter((_, i) => i !== index));
    };

    const getTypeBadge = (type: KnowledgeBaseType) => {
        const typeLabels: Record<KnowledgeBaseType, string> = {
            general: t('types.general'),
            documents: t('types.documents'),
            routes: t('types.routes'),
            regulation: t('types.regulation'),
            'company/account-specific': t('types.companyAccountSpecific'),
        };
        return <Badge color="gray" size="sm">{typeLabels[type]}</Badge>;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (isError || !knowledgeBase) {
        return (
            <div className="space-y-6">
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                    <p className="text-tertiary mb-4">{t('noData.message')}</p>
                    <Button onClick={() => navigate('/admin/knowledge')} size="md" color="secondary">
                        {t('detail.backToList')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link
                    to="/admin/knowledge"
                    className="text-tertiary hover:text-primary"
                >
                    {t('title')}
                </Link>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{knowledgeBase.name}</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">
                        {isEditing ? t('form.edit') : knowledgeBase.name}
                    </h2>
                    <p className="text-tertiary mt-1">{t('detail.title')}</p>
                </div>
                <div className="flex items-center gap-2">
                    {!isEditing ? (
                        <>
                            <Button
                                onClick={() => setIsEditing(true)}
                                size="md"
                                color="secondary"
                            >
                                {t('actions.edit')}
                            </Button>
                            <Button
                                onClick={() => navigate('/admin/knowledge')}
                                size="md"
                                color="secondary"
                                iconLeading={ArrowLeft}
                            >
                                {t('detail.backToList')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={() => {
                                    setIsEditing(false);
                                    setFormData({
                                        name: knowledgeBase.name,
                                        description: knowledgeBase.description || '',
                                        kb_type: knowledgeBase.kb_type,
                                    });
                                    setUploadFiles([]);
                                    setUploadOptions({
                                        priority: undefined,
                                        access_level: undefined,
                                        chunking_params: undefined,
                                    });
                                    setShowAdvanced(false);
                                }}
                                size="md"
                                color="secondary"
                            >
                                {t('form.cancel')}
                            </Button>
                            <Button
                                onClick={handleSave}
                                size="md"
                                color="primary"
                                isDisabled={!formData.name.trim() || !formData.kb_type || updateKnowledgeBase.isPending || uploadDocument.isPending}
                            >
                                {(updateKnowledgeBase.isPending || uploadDocument.isPending) ? t('loading') : t('form.save')}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Form or Display */}
            {isEditing ? (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary space-y-6">
                    <div className="space-y-4">
                        <Input
                            label={t('form.fields.name')}
                            placeholder={t('form.fields.namePlaceholder')}
                            value={formData.name}
                            onChange={(value) => setFormData({ ...formData, name: value })}
                            isRequired
                            size="md"
                        />

                        <Input
                            label={t('form.fields.description')}
                            placeholder={t('form.fields.descriptionPlaceholder')}
                            value={formData.description}
                            onChange={(value) => setFormData({ ...formData, description: value })}
                            size="md"
                        />

                        <Select
                            label={t('form.fields.type')}
                            placeholder={t('form.fields.typePlaceholder')}
                            items={typeOptions}
                            selectedKey={formData.kb_type}
                            onSelectionChange={(key) => setFormData({ ...formData, kb_type: key as KnowledgeBaseType | '' })}
                            isRequired
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>

                    {/* Documents Upload */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-primary">{t('form.documents.title')}</h3>

                        <div
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ? 'border-brand bg-brand/10' : 'border-secondary'
                                }`}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <Upload01 className="size-12 mx-auto mb-4 text-tertiary" />
                            <p className="text-primary mb-2">{t('form.documents.uploadArea')}</p>
                            <p className="text-sm text-tertiary mb-4">{t('form.documents.supportedFormats')}</p>
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                size="md"
                                color="secondary"
                            >
                                {t('form.documents.selectFile')}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".pdf,.docx,.txt,.md,.json"
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                        </div>

                        {uploadFiles.length > 0 && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-primary">{t('form.documents.selectedFiles')}</label>
                                <div className="space-y-1">
                                    {uploadFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between rounded-lg bg-secondary p-2"
                                        >
                                            <span className="text-sm text-primary">{file.name}</span>
                                            <button
                                                onClick={() => handleRemoveFile(index)}
                                                className="text-error hover:text-error_hover"
                                            >
                                                <XCircle className="size-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Advanced Options */}
                        {uploadFiles.length > 0 && (
                            <div className="border border-secondary rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className="w-full flex items-center justify-between p-3 text-left hover:bg-secondary/50 transition-colors rounded-lg"
                                >
                                    <span className="text-sm font-medium text-primary">
                                        {t('form.documents.advancedOptions', { defaultValue: 'Advanced Options' })}
                                    </span>
                                    {showAdvanced ? (
                                        <ChevronUp className="size-4 text-tertiary" />
                                    ) : (
                                        <ChevronDown className="size-4 text-tertiary" />
                                    )}
                                </button>

                                {showAdvanced && (
                                    <div className="p-4 pt-0 space-y-4 border-t border-secondary">
                                        <p className="text-xs text-tertiary mt-2">
                                            {t('form.documents.advancedOptionsHint', { defaultValue: 'These settings apply to all selected files. Leave empty to use defaults.' })}
                                        </p>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Select
                                                label={t('form.documents.priorityLabel', { defaultValue: 'Priority' })}
                                                placeholder={t('form.documents.priorityPlaceholder', { defaultValue: 'Select priority (optional)' })}
                                                items={priorityOptions}
                                                selectedKey={uploadOptions.priority?.toString() || ''}
                                                onSelectionChange={(key) => setUploadOptions({
                                                    ...uploadOptions,
                                                    priority: key ? parseInt(key as string) : undefined,
                                                })}
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>

                                            <Select
                                                label={t('form.documents.accessLevelLabel', { defaultValue: 'Access Level' })}
                                                placeholder={t('form.documents.accessLevelPlaceholder', { defaultValue: 'Select access level (optional)' })}
                                                items={accessLevelOptions}
                                                selectedKey={uploadOptions.access_level || ''}
                                                onSelectionChange={(key) => setUploadOptions({
                                                    ...uploadOptions,
                                                    access_level: key ? key as KnowledgeDocumentAccessLevel : undefined,
                                                })}
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                {t('form.documents.chunkingParams', { defaultValue: 'Chunking Parameters' })}
                                            </label>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <Input
                                                    label={t('form.documents.chunkSize', { defaultValue: 'Chunk Size' })}
                                                    placeholder={t('form.documents.chunkSizePlaceholder', { defaultValue: 'e.g., 1000' })}
                                                    type="number"
                                                    value={uploadOptions.chunking_params?.chunk_size?.toString() || ''}
                                                    onChange={(value) => setUploadOptions({
                                                        ...uploadOptions,
                                                        chunking_params: {
                                                            ...uploadOptions.chunking_params,
                                                            chunk_size: value ? parseInt(value) : undefined,
                                                        },
                                                    })}
                                                    size="md"
                                                />
                                                <Input
                                                    label={t('form.documents.chunkOverlap', { defaultValue: 'Chunk Overlap' })}
                                                    placeholder={t('form.documents.chunkOverlapPlaceholder', { defaultValue: 'e.g., 200' })}
                                                    type="number"
                                                    value={uploadOptions.chunking_params?.chunk_overlap?.toString() || ''}
                                                    onChange={(value) => setUploadOptions({
                                                        ...uploadOptions,
                                                        chunking_params: {
                                                            ...uploadOptions.chunking_params,
                                                            chunk_overlap: value ? parseInt(value) : undefined,
                                                        },
                                                    })}
                                                    size="md"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-tertiary">{t('headers.name')}</label>
                            <p className="text-primary">{knowledgeBase.name}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-tertiary">{t('headers.type')}</label>
                            <div>{getTypeBadge(knowledgeBase.kb_type)}</div>
                        </div>
                        <div className="col-span-2">
                            <label className="text-sm font-medium text-tertiary">{t('headers.description')}</label>
                            <p className="text-primary">{knowledgeBase.description || '-'}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-tertiary">{t('headers.documentCount')}</label>
                            <p className="text-primary">{knowledgeBase.document_count}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-tertiary">{t('headers.createdAt')}</label>
                            <p className="text-primary">{new Date(knowledgeBase.created_at).toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents List */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-primary">{t('detail.documents')}</h3>
                    {!isEditing && (
                        <Button
                            onClick={() => setIsEditing(true)}
                            size="sm"
                            color="primary"
                            iconLeading={Upload01}
                        >
                            {t('detail.uploadDocument')}
                        </Button>
                    )}
                </div>

                {documents.length === 0 ? (
                    <p className="text-tertiary text-center py-8">{t('detail.noDocuments')}</p>
                ) : (
                    <TableCard.Root>
                        <Table>
                            <Table.Header>
                                <Table.Head label="Title" isRowHeader />
                                <Table.Head label="Priority" />
                                <Table.Head label="Access Level" />
                                <Table.Head label="Chunks" />
                                <Table.Head label="Retrievals" />
                                <Table.Head label="Actions" />
                            </Table.Header>
                            <Table.Body>
                                {documents.map((doc) => (
                                    <Table.Row key={doc.doc_id}>
                                        <Table.Cell>
                                            <div className="font-medium text-primary">{doc.title}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color="gray" size="sm">{doc.priority}</Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge
                                                color={doc.access_level === 'public' ? 'success' : doc.access_level === 'company' ? 'blue' : 'warning'}
                                                size="sm"
                                            >
                                                {doc.access_level}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-primary">{doc.chunk_count}</span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-primary">{doc.retrieval_count}</span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Button
                                                onClick={() => handleDeleteDocument(doc.doc_id)}
                                                size="sm"
                                                color="secondary-destructive"
                                                iconLeading={Trash01}
                                                isDisabled={deleteDocument.isPending}
                                            >
                                                {deleteDocument.isPending ? t('loading') : t('form.documents.remove')}
                                            </Button>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                )}
            </div>
        </div>
    );
};
